/**
 * Global Properties Service
 * 
 * Service for managing global property definitions and their relationships.
 * Provides access to all global properties API endpoints.
 */

import { apiClient } from '../apiClient';
import {
  PropertyDefinition,
  PropertyDefinitionResponse,
  PropertySummary,
  PropertyDependenciesResponse,
  PropertyValidationRulesResponse,
  PropertyStateAvailabilityResponse,
  CalculatePropertyRequest,
  CalculatePropertyResponse,
  PropertyValidationResponse,
  AvailablePropertiesResponse,
  BalanceSheetValidationResponse,
  ProductSituationAlignmentRequest,
  ProductSituationAlignmentResponse,
  ActorSituationAlignmentRequest,
  ActorSituationAlignmentResponse,
  DealScoreResponse,
  ImportGlobalPropertiesResponse,
  TransformationLogic,
  ApiError
} from '../../types/globalProperties';

export class GlobalPropertiesService {
  /**
   * Create a new property definition
   */
  static async createPropertyDefinition(propertyDefinition: PropertyDefinition): Promise<PropertyDefinitionResponse> {
    try {
      // Clean up the payload: remove undefined values and empty objects
      const cleanedPayload: any = { ...propertyDefinition };
      
      // Transform default_value: backend expects Optional[Dict[str, Any]] (object/dict or None)
      // If default_value is a primitive (string/number/boolean), we need to convert it
      if (cleanedPayload.default_value !== undefined && cleanedPayload.default_value !== null) {
        // If it's already an object/dict (and not an array), keep it as is
        if (typeof cleanedPayload.default_value === 'object' && !Array.isArray(cleanedPayload.default_value)) {
          // Already an object/dict, keep it
        } else if (typeof cleanedPayload.default_value === 'string' && cleanedPayload.default_value.trim() === '') {
          // Empty string should be omitted
          delete cleanedPayload.default_value;
        } else {
          // For primitive values (string, number, boolean), backend expects Dict[str, Any]
          // Since backend diagnosis says it expects a dict, we'll wrap primitive values
          // Common pattern: wrap in { "value": ... } or convert to null
          // Trying wrapping approach first - if this doesn't work, we can try null
          cleanedPayload.default_value = { value: cleanedPayload.default_value };
        }
      } else {
        // undefined or null - remove it (backend will treat as None)
        delete cleanedPayload.default_value;
      }
      
      // Remove undefined values
      Object.keys(cleanedPayload).forEach(key => {
        if (cleanedPayload[key] === undefined) {
          delete cleanedPayload[key];
        }
      });
      
      // Remove empty validation_rules object
      if (cleanedPayload.validation_rules && Object.keys(cleanedPayload.validation_rules).length === 0) {
        delete cleanedPayload.validation_rules;
      }
      
      // State availability is deprecated - no longer needed
      
      // Remove empty calculation_dependencies
      if (cleanedPayload.calculation_dependencies && 
          (!cleanedPayload.calculation_dependencies.type || 
           !cleanedPayload.calculation_dependencies.source_properties ||
           Object.keys(cleanedPayload.calculation_dependencies.source_properties).length === 0)) {
        delete cleanedPayload.calculation_dependencies;
      }
      
      console.log('[GlobalPropertiesService] Creating property with payload:', JSON.stringify(cleanedPayload, null, 2));
      
      const response = await apiClient.post<PropertyDefinitionResponse>(
        '/properties',
        cleanedPayload
      );
      return response.data;
    } catch (error: any) {
      console.error('[GlobalPropertiesService] Error creating property:', error);
      console.error('[GlobalPropertiesService] Error response:', error.response?.data);
      console.error('[GlobalPropertiesService] Error detail array:', JSON.stringify(error.response?.data?.detail, null, 2));
      console.error('[GlobalPropertiesService] Request payload was:', propertyDefinition);
      
      if (error.response?.status === 400 || error.response?.status === 422) {
        const detail = error.response?.data?.detail;
        let errorMessage: string;
        
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map((err: any) => {
            const field = err.loc?.join('.') || 'unknown';
            const msg = err.msg || 'validation error';
            const type = err.type || '';
            return `${field}: ${msg}${type ? ` (${type})` : ''}`;
          }).join('\n');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail) || 'Invalid property definition';
        }
        
        console.error('[GlobalPropertiesService] Validation errors:', errorMessage);
        throw new ApiError(error.response.status, errorMessage, '/properties');
      }
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to create property definition',
        '/properties'
      );
    }
  }

  /**
   * Get property definition for a specific property key
   */
  static async getPropertyDefinition(propertyKey: string): Promise<PropertyDefinitionResponse> {
    try {
      const response = await apiClient.get<PropertyDefinitionResponse>(
        `/properties/${encodeURIComponent(propertyKey)}`
      );
      console.log('[GlobalPropertiesService] getPropertyDefinition response:', {
        propertyKey,
        hasDefinition: !!response.data.definition,
        definitionKeys: response.data.definition ? Object.keys(response.data.definition) : [],
        hasChangeTypeDefs: response.data.definition ? 'change_type_definitions' in response.data.definition : false,
        changeTypeDefsValue: response.data.definition?.change_type_definitions,
        changeTypeDefsType: typeof response.data.definition?.change_type_definitions,
        fullDefinition: response.data.definition,
        fullDefinitionStringified: JSON.stringify(response.data.definition, null, 2),
        fullResponseStringified: JSON.stringify(response.data, null, 2)
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new ApiError(404, `Property definition '${propertyKey}' not found`, `/properties/${propertyKey}`);
      }
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to get property definition',
        `/properties/${propertyKey}`
      );
    }
  }

  /**
   * Get all properties that a property depends on for calculation
   */
  static async getPropertyDependencies(propertyKey: string): Promise<PropertyDependenciesResponse> {
    try {
      const response = await apiClient.get<PropertyDependenciesResponse>(
        `/properties/${encodeURIComponent(propertyKey)}/dependencies`
      );
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to get property dependencies',
        `/properties/${propertyKey}/dependencies`
      );
    }
  }

  /**
   * Get validation rules for a property
   */
  static async getPropertyValidationRules(propertyKey: string): Promise<PropertyValidationRulesResponse> {
    try {
      const response = await apiClient.get<PropertyValidationRulesResponse>(
        `/properties/${encodeURIComponent(propertyKey)}/validation-rules`
      );
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to get validation rules',
        `/properties/${propertyKey}/validation-rules`
      );
    }
  }

  /**
   * Get state availability rules for a property
   */
  static async getPropertyStateAvailability(propertyKey: string): Promise<PropertyStateAvailabilityResponse> {
    try {
      const response = await apiClient.get<PropertyStateAvailabilityResponse>(
        `/properties/${encodeURIComponent(propertyKey)}/state-availability`
      );
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to get state availability',
        `/properties/${propertyKey}/state-availability`
      );
    }
  }

  /**
   * Calculate a property value based on its dependencies
   */
  static async calculateProperty(
    itemId: string,
    propertyKey: string,
    transformationLogic?: TransformationLogic
  ): Promise<CalculatePropertyResponse> {
    try {
      const body: CalculatePropertyRequest = {};
      if (transformationLogic) {
        body.transformation_logic = transformationLogic;
      }

      const response = await apiClient.post<CalculatePropertyResponse>(
        `/artifact-items/${itemId}/properties/${encodeURIComponent(propertyKey)}/calculate`,
        body
      );
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to calculate property',
        `/artifact-items/${itemId}/properties/${propertyKey}/calculate`
      );
    }
  }

  /**
   * Validate property values against their validation rules
   */
  static async validateProperties(
    itemId: string,
    propertyKeys?: string[]
  ): Promise<PropertyValidationResponse> {
    try {
      const params = new URLSearchParams();
      if (propertyKeys && propertyKeys.length > 0) {
        propertyKeys.forEach(key => params.append('property_keys', key));
      }

      const url = `/artifact-items/${itemId}/properties/validate${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.post<PropertyValidationResponse>(url);
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to validate properties',
        `/artifact-items/${itemId}/properties/validate`
      );
    }
  }

  /**
   * Get properties that are available, required, hidden, or readonly in a specific artifact state
   */
  static async getAvailablePropertiesForState(
    artifactId: string,
    stateName: string
  ): Promise<AvailablePropertiesResponse> {
    try {
      const response = await apiClient.get<AvailablePropertiesResponse>(
        `/artifacts/${artifactId}/states/${encodeURIComponent(stateName)}/properties`
      );
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to get available properties',
        `/artifacts/${artifactId}/states/${stateName}/properties`
      );
    }
  }

  /**
   * Validate that a balance sheet equation is balanced (Assets = Liabilities + Equity)
   */
  static async validateBalanceSheet(itemId: string): Promise<BalanceSheetValidationResponse> {
    try {
      const response = await apiClient.post<BalanceSheetValidationResponse>(
        `/artifact-items/${itemId}/balance-sheet/validate`
      );
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to validate balance sheet',
        `/artifact-items/${itemId}/balance-sheet/validate`
      );
    }
  }

  /**
   * Calculate alignment percentage between a product (solution) and a situation
   */
  static async calculateProductSituationAlignment(
    request: ProductSituationAlignmentRequest
  ): Promise<ProductSituationAlignmentResponse> {
    try {
      const response = await apiClient.post<ProductSituationAlignmentResponse>(
        '/alignment/calculate-product-situation',
        request
      );
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to calculate product-situation alignment',
        '/alignment/calculate-product-situation'
      );
    }
  }

  /**
   * Calculate alignment percentage for an actor situation
   */
  static async calculateActorSituationAlignment(
    request: ActorSituationAlignmentRequest
  ): Promise<ActorSituationAlignmentResponse> {
    try {
      const response = await apiClient.post<ActorSituationAlignmentResponse>(
        '/alignment/calculate-actor-situation',
        request
      );
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to calculate actor-situation alignment',
        '/alignment/calculate-actor-situation'
      );
    }
  }

  /**
   * Calculate deal score from alignment and value components
   */
  static async calculateDealScore(
    productSituationAlignment: number,
    productSituationValue: number,
    actorSituationAlignment: number
  ): Promise<DealScoreResponse> {
    try {
      const params = new URLSearchParams();
      params.append('product_situation_alignment', productSituationAlignment.toString());
      params.append('product_situation_value', productSituationValue.toString());
      params.append('actor_situation_alignment', actorSituationAlignment.toString());

      const response = await apiClient.post<DealScoreResponse>(
        `/alignment/calculate-deal-score?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to calculate deal score',
        '/alignment/calculate-deal-score'
      );
    }
  }

  /**
   * Import global properties from a JSON catalog file
   */
  static async importGlobalProperties(
    catalogFile?: File,
    catalogPath?: string
  ): Promise<ImportGlobalPropertiesResponse> {
    try {
      const formData = new FormData();
      if (catalogFile) {
        formData.append('catalog_file', catalogFile);
      }

      const params = new URLSearchParams();
      if (catalogPath) {
        params.append('catalog_path', catalogPath);
      }

      const url = `/global-properties/import${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.post<ImportGlobalPropertiesResponse>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to import global properties',
        '/global-properties/import'
      );
    }
  }

  /**
   * List global property definitions
   */
  static async listPropertyDefinitions(params?: {
    skip?: number;
    limit?: number;
    property_type?: string;
    search?: string;
    summary?: boolean;
  }): Promise<{
    properties: PropertyDefinitionResponse[] | PropertySummary[];
    total: number;
    skip: number;
    limit: number;
    isSummary?: boolean;
  }> {
    if (process.env.NODE_ENV === 'development') {
      console.log('[GlobalPropertiesService] ===== listPropertyDefinitions START =====');
      console.log('[GlobalPropertiesService] Called with params:', JSON.stringify(params, null, 2));
    }
    try {
      const url = '/properties';
      const fullUrl = `${apiClient.defaults.baseURL || ''}${url}`;
      if (process.env.NODE_ENV === 'development') {
        console.log('[GlobalPropertiesService] Making GET request to:', fullUrl, 'with params:', params);
      }
      
      const response = await apiClient.get(url, { params });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[GlobalPropertiesService] Response received:', {
          status: response.status,
          statusText: response.statusText,
          dataType: typeof response.data,
          isArray: Array.isArray(response.data),
          dataLength: Array.isArray(response.data) ? response.data.length : (response.data as any)?.items?.length || 0
        });
      }
      
      const data = response.data;
      const isSummary = params?.summary === true;

      // Handle summary response format: {properties: PropertySummary[], total, skip, limit}
      if (isSummary && data?.properties) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[GlobalPropertiesService] Summary mode: Found properties array with', data.properties.length, 'items');
        }
        
        return {
          properties: data.properties as PropertySummary[],
          total: data.total || data.properties.length,
          skip: data.skip || 0,
          limit: data.limit || data.properties.length,
          isSummary: true
        };
      }

      // Handle full response format: {properties: PropertyDefinition[], total, skip, limit}
      // Transform to PropertyDefinitionResponse[]
      let result: PropertyDefinitionResponse[];
      if (Array.isArray(data)) {
        result = data as PropertyDefinitionResponse[];
        return {
          properties: result,
          total: result.length,
          skip: 0,
          limit: result.length,
          isSummary: false
        };
      } else if (Array.isArray(data?.items)) {
        result = data.items as PropertyDefinitionResponse[];
        return {
          properties: result,
          total: data.total || result.length,
          skip: data.skip || 0,
          limit: data.limit || result.length,
          isSummary: false
        };
      } else if (Array.isArray(data?.properties)) {
        // Backend returns {properties: [...], total, skip, limit}
        // Each item in properties is a PropertyDefinition, not PropertyDefinitionResponse
        // We need to wrap them: {property_key: string, definition: PropertyDefinition}
        if (process.env.NODE_ENV === 'development') {
          console.log('[GlobalPropertiesService] Found properties array with', data.properties.length, 'items');
        }
        
        result = data.properties.map((def: PropertyDefinition) => ({
          property_key: def.property_key,
          definition: def
        })) as PropertyDefinitionResponse[];
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[GlobalPropertiesService] Transformed to PropertyDefinitionResponse format with', result.length, 'items');
        }
        
        return {
          properties: result,
          total: data.total || result.length,
          skip: data.skip || 0,
          limit: data.limit || result.length,
          isSummary: false
        };
      } else {
        console.warn('[GlobalPropertiesService] Unexpected response shape:', {
          dataType: typeof data,
          isArray: Array.isArray(data),
          keys: data ? Object.keys(data) : [],
          data
        });
        return {
          properties: [],
          total: 0,
          skip: 0,
          limit: 0,
          isSummary: false
        };
      }
    } catch (error: any) {
      console.error('[GlobalPropertiesService] Error in listPropertyDefinitions:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        response: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          headers: error.response?.headers,
          data: error.response?.data
        },
        request: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          params: error.config?.params,
          headers: error.config?.headers
        }
      });
      throw new ApiError(
        error.response?.status || 500,
        error.response?.data?.detail || error.message || 'Failed to list property definitions',
        '/properties'
      );
    }
  }

  /**
   * Get available assessment elements from the backend
   */
  static async getAssessmentElements(): Promise<Array<{ value: string; label: string }>> {
    try {
      const response = await apiClient.get('/assessment-elements');
      const elements = response.data || [];
      // Assume response is array of strings or array of objects with value/label
      return elements.map((element: string | { value: string; label: string }) => {
        if (typeof element === 'string') {
          return { value: element, label: element };
        }
        return element;
      });
    } catch (error) {
      console.error('[GlobalPropertiesService] Error fetching assessment elements:', error);
      // Return empty array on error to prevent UI breaking
      return [];
    }
  }
}

